import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7c873e88"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  role: "dialog",
  "aria-modal": "true",
  class: "relative z-30"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "flex-grow" }, null, -1))
const _hoisted_3 = { class: "relative flex flex-grow flex-col overflow-y-hidden xl:flex-row" }

import { ComputedRef, Ref, computed, nextTick, onMounted, ref } from 'vue';
import { Position, UseDraggableReturn, clamp, useDraggable, useElementBounding, useResizeObserver } from '@vueuse/core';
import { UseFocusTrapReturn, useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { VueAttributeStyle } from '@/types/attributes-vue';
import AppModalBackdrop from '@/components/Base/AppModal/AppModalBackdrop.vue';
import AppModalClose from '@/components/Base/AppModal/AppModalClose.vue';
import AppModalTitle from '@/components/Base/AppModal/AppModalTitle.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppModal',
  props: {
  title: {
    type: String,
    required: true,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  fullsize: {
    type: Boolean,
    default: false,
  },
},
  emits: {
  close: null,
},
  setup(__props, { emit }) {

const props = __props;





const container: Ref<HTMLElement | null> = ref(null);
const modal: Ref<HTMLElement | null> = ref(null);
const focus: Ref<HTMLElement | null> = ref(null);
const handle: Ref<HTMLElement | null> = ref(null);

const fallbackFocus: Ref<HTMLElement> = ref((document.activeElement as HTMLElement) || document.body);

let x: Ref<number> = ref(0);
let y: Ref<number> = ref(0);

const { left, right, top, bottom } = useElementBounding(container);
const { width, height } = useElementBounding(modal);

const focusTrap: UseFocusTrapReturn = useFocusTrap(modal, {
  escapeDeactivates: false,
  fallbackFocus: fallbackFocus.value,
  initialFocus(): HTMLElement {
    return focus.value || fallbackFocus.value;
  },
});

if (props.draggable) {
  const draggable: UseDraggableReturn = useDraggable(modal, {
    preventDefault: true,
    handle: handle,
    onStart(position: Position, event: PointerEvent): void | false {
      const target: HTMLElement = event.target as HTMLElement;

      if (target.classList.contains('modal-handle-ignore')) {
        return false;
      }

      document.body.classList.add('cursor-move');
    },
    onEnd(): void {
      document.body.classList.remove('cursor-move');
    },
  });

  x = draggable.x;
  y = draggable.y;
}

const modalX: ComputedRef<number> = computed((): number => {
  return clamp(left.value, x.value, right.value - width.value);
});

const modalY: ComputedRef<number> = computed((): number => {
  return clamp(top.value, y.value, bottom.value - height.value);
});

const modalStyle: ComputedRef<VueAttributeStyle> = computed((): VueAttributeStyle => {
  return {
    left: `${modalX.value}px`,
    top: `${modalY.value}px`,
  };
});

const modalDefaultPosition = (): void => {
  x.value = (right.value - width.value) / 2;
  y.value = (bottom.value - height.value) / 2;
};

const modalClose = (): void => {
  focusTrap.deactivate();
  emit('close');
};

onMounted((): void => {
  modalDefaultPosition();

  nextTick((): void => {
    focusTrap.activate();
  });
});

useResizeObserver(container, (): void => {
  modalDefaultPosition();
});

useResizeObserver(modal, (): void => {
  modalDefaultPosition();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppModalBackdrop),
    _createElementVNode("div", {
      ref_key: "container",
      ref: container,
      class: "fixed inset-0"
    }, [
      _createElementVNode("div", {
        ref_key: "modal",
        ref: modal,
        style: _normalizeStyle(_unref(modalStyle)),
        class: "fixed"
      }, [
        _createElementVNode("div", {
          class: _normalizeClass({
            'flex flex-col overflow-hidden rounded-none border-0 border-brand-600 shadow-xl xl:rounded-lg xl:border': true,
            'modal-active-fullsize': props.fullsize,
            'modal-inactive-fullsize': !props.fullsize,
          })
        }, [
          _createElementVNode("span", {
            ref_key: "focus",
            ref: focus,
            "aria-label": "Modal",
            tabindex: "0"
          }, null, 512),
          _createElementVNode("div", {
            ref_key: "handle",
            ref: handle,
            class: _normalizeClass({
              'flex items-center bg-dark-400 px-5 py-2 shadow-lg': true,
              'modal-handle-draggable': props.draggable,
            })
          }, [
            _createVNode(AppModalTitle, {
              title: props.title
            }, null, 8, ["title"]),
            _hoisted_2,
            _createVNode(AppModalClose, { onClose: modalClose })
          ], 2),
          _createElementVNode("div", _hoisted_3, [
            _renderSlot(_ctx.$slots, "default")
          ])
        ], 2)
      ], 4)
    ], 512)
  ]))
}
}

})